<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import ControlCustomerPageTab from '../components/ControlCustomerPageTab.vue';
import { CUSTOMER_TAB_ROUTER_INFO, DownloadMenuItems } from '../constants';
import { useBusinessListForecastForReceiptStore } from './useBusinessListForecastForReceiptStore';
import { onMounted, computed, onBeforeMount, ref } from 'vue';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { i18n } from '@/i18n';
import EditForecastModal from '../components/EditForecastModal.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import SelectInput from '@/corePagesModule/SelectInput';
import { SupplierForecastRepository } from '@/coreInfrastructureModule/Repositories/SupplierForecastRepository';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import { ForecastForReceiptMenuService } from '../domains/services/forecastForReceiptMenuService';
import ForecastForReceiptSelectInput from '../components/ForecastForReceiptSelectInput.vue';
import ForecastForReceiptMenuDropdown from '../components/ForecastForReceiptMenuDropdown.vue';
import AddBaseBusinessForecastForReceiptModal from '../components/editForecastForReceiptModal/addBaseBusinessForecastForReceipt/AddBaseBusinessForecastForReceiptModal.vue';
import CreateForecastForReceiptModal from '../components/editForecastForReceiptModal/createForecastForReceipt/CreateForecastForReceiptModal.vue';
import UpdateForecastForReceiptModal from '../components/editForecastForReceiptModal/editForecastForReceipt/UpdateForecastForReceiptModal.vue';
import EditForecastForReceiptModal from '../components/editForecastForReceiptModal/editForecastForReceipt/EditForecastForReceiptModal.vue';
import DownloadForecastForReceiptModal from '../components/DownloadForecastForReceiptModal.vue';
import DownloadMenuDropdown from '../components/DownloadMenuDropdown.vue';
import DownloadCustomerForecastModal from '../components/DownloadCustomerForecastModal.vue';

const t = i18n.t;
const route = useRoute();
const store = useBusinessListForecastForReceiptStore();
const { state } = storeToRefs(store);
const locale = computed(() => i18n.locale);

const {
  onClosedForecastForReceiptActionModal,
  onSelectedForecastForReceiptMenu,
  onAppliedCreateBaseBusinessForecastForReceipt,
  onAppliedAddBaseBusinessForecastForReceipt,
  onAppliedEditBaseBusinessForecastForReceipt,
  onAppliedUpdateBaseBusinessForecastForReceipt,
  onSwitchedEditTargetForecastForReceipt,
  onClickedDeleteFromForecastForReceiptButton,
  onClickedDeleteForecastForReceiptInEditMode,
  onConfirmedDeleteForecastForReceiptInEditMode,
  onCanceledDeleteForecastForReceiptInEditMode,
  onClickRemoveFromForecastInRow,
  onBackToNormalMode,
  getTemplateCustomerForecastExcelDownload,
  updateSelectedForecastForReceiptFormState,
  getFilterItemsValue,
  openDownloadCustomerForecastModal,
  closeDownloadCustomerForecastModal,
  onApplyDownloadCustomerForecast,
} = store;

const supplierForecastRepository = new SupplierForecastRepository();

const isRequiredReloadForecastForReceipts = computed(() => {
  return ForecastForReceiptMenuService.needReloadOnAfterAction(state.value.forecastForReceiptLoadingState.type);
});

onBeforeMount(async () => {
  store.init();

  // ダッシュボードの取引一覧ボタンからの遷移時、受領用内示IDがパスパラメータで渡される
  const forecastForReceiptId = typeof route.params.forecastForReceiptId === 'string' ? route.params.forecastForReceiptId : undefined;
  if (forecastForReceiptId) {
    // 受領用内示を選択状態にする
    await onSwitchedEditTargetForecastForReceipt(forecastForReceiptId);
  }
});

/**
 * 仕入先内示作成ボタンの活性/非活性を切り替える
 * 受領用内示を1つ以上選択している場合のみ有効
 */
const isDisabledCreateForecast = computed((): boolean => {
  return store.tableContentItems === undefined || store.tableContentItems.length === 0;
});

const monthsOptionsDisplayForecast = computed(() => {
  return store.monthsOptionsDisplayForecastNumber.map((monthsOption) => monthsOption + t('unitMonth'));
});

/**
 * 受領用内示編集ボタンの活性/非活性を切り替える
 * 受領用内示を1つ以上選択している場合のみ有効
 */
const isDisabledEditForecast = computed((): boolean => {
  return !state.value.tableContents.checkedDisplayIds || state.value.tableContents.checkedDisplayIds.length === 0;
});

/**
 * ダウンロードメニュードロップダウンの開閉状態
 */
const isOpenDownloadMenuDropdown = ref(false);

/**
 * ダウンロードメニューの項目が選択された際の処理
 * @param item - 選択されたダウンロードメニュー項目
 */
const onSelectedDownloadMenu = (item: DownloadMenuItems) => {
  switch (item) {
    case 'ForecastForReceiptFormat':
      // 受領用内示出力モーダルを表示
      state.value.downloadForecastForReceiptModalState.modalState = true;
      break;
    case 'SalesResultAndCustomerForecast':
      // 客先内示ダウンロードモーダルを表示
      openDownloadCustomerForecastModal();
      break;
  }
};
</script>

<template>
  <div class="receive-forecast-business">
    <div class="receive-forecast-business__top-area top-area">
      <ControlCustomerPageTab class="top-area__tab-bar" :tab-info-object="[...CUSTOMER_TAB_ROUTER_INFO]" />
      <div class="top-area__button-area button-area">
        <template v-if="state.pageMode === 'editForecastForReceipt'">
          <div class="button-area__edit-forecast-for-receipt-button edit-forecast-for-receipt-button">
            <TextButton
              class="edit-forecast-for-receipt-button--update-name-button"
              :button-style="'btn-primary'"
              :size="''"
              :disabled="state.editForecastForReceiptModeState.selectedEditForecastForReceiptId === ''"
              @click="() => (state.editForecastForReceiptModeState.updateForecastForReceiptModalState = true)"
            >
              {{ t('受領用内示情報を編集') }}
            </TextButton>
            <TextButton
              class="edit-forecast-for-receipt-button--delete-from-group-button"
              :button-style="'btn-primary'"
              :size="''"
              :disabled="state.tableContents.checkedDisplayIds.length === 0"
              @click="() => onClickedDeleteFromForecastForReceiptButton()"
            >
              {{ t('受領用内示情報から外す') }}
            </TextButton>
            <TextButton
              class="edit-forecast-for-receipt-button--delete-forecast-button"
              :button-style="'btn-primary'"
              :size="''"
              :disabled="
                state.editForecastForReceiptModeState.selectedEditForecastForReceiptId === '' ||
                state.tableContents.loadingState.status.type !== 'zeroMatch'
              "
              @click="onClickedDeleteForecastForReceiptInEditMode()"
            >
              {{ t('受領用内示を削除') }}
            </TextButton>
            <TextButton
              class="edit-forecast-for-receipt-button--cancel-button"
              :button-style="'btn-cancel'"
              :size="''"
              @click="onBackToNormalMode()"
            >
              {{ t('元のページに戻る') }}
            </TextButton>
          </div>
        </template>
        <ForecastForReceiptMenuDropdown
          v-else
          class="forecast-for-receipt-menu-button forecast-for-receipt-menu-button"
          :is-selected-businesses="state.tableContents.checkedDisplayIds.length > 0"
          :is-open-prop="state.isOpenForecastForReceiptMenuDropdown"
          :selected-item-prop="state.selectedForecastForReceiptMenu"
          @click:select-input="() => (state.isOpenForecastForReceiptMenuDropdown = true)"
          @close="() => (state.isOpenForecastForReceiptMenuDropdown = false)"
          @selected-item="
            (item) => {
              onSelectedForecastForReceiptMenu(item);
            }
          "
        />
        <TextButton
          class="button-area__edit-base-business-responsible-user__button edit-base-business-responsible-user__button button-area__button button-area__button--fill btn-primary"
          :size="''"
          :disabled="isDisabledEditForecast"
          @click="state.editForecastModalState = true"
        >
          <div class="edit-base-business-responsible-user__button--container">
            <p class="edit-base-business-responsible-user__button--text">{{ t('取引担当者編集') }}</p>
            <SvgLoader class="edit-base-business-responsible-user__button--icon" name="editPen" />
          </div>
        </TextButton>
        <TextButton
          class="button-area__create-receive-forecast-button create-receive-forecast-button button-area__button button-area__button--fill btn-primary"
          :size="''"
          :disabled="isDisabledCreateForecast"
          @click="state.createForecastModalState = true"
        >
          <div class="create-receive-forecast-button__container">
            <p class="create-receive-forecast-button__text">{{ t('forecastForReceipt_createSupplierForecast') }}</p>
            <SvgLoader class="create-receive-forecast-button__icon" name="plusIconWhite" />
          </div>
        </TextButton>
        <!-- ダウンロードメニュードロップダウン -->
        <DownloadMenuDropdown
          class="button-area__download-menu-dropdown"
          :is-open-prop="isOpenDownloadMenuDropdown"
          @click:select-input="() => (isOpenDownloadMenuDropdown = true)"
          @close="() => (isOpenDownloadMenuDropdown = false)"
          @selected-item="onSelectedDownloadMenu"
        />
        <!-- <div class="button-area__delete-receive-forecast-button" /> -->
      </div>
    </div>
    <div class="receive-forecast-business__filter-content filter-content">
      <!-- 受領用内示の選択用UI -->
      <ForecastForReceiptSelectInput
        class="filter-content__edit-forecast-for-receipt--select-input"
        :select-input-index="99"
        :store-tag="'editForecastForReceiptModeInventoryControl'"
        :selected-forecast-for-receipt-id-prop="state.editForecastForReceiptModeState.selectedEditForecastForReceiptId"
        :reload="isRequiredReloadForecastForReceipts"
        :use-reset="true"
        use-teleport
        @selected-forecast-for-receipt="(forecastForReceiptId) => onSwitchedEditTargetForecastForReceipt(forecastForReceiptId)"
        @selected-forecast-for-receipt-form-state="
          (forecastForReceiptFormState) => updateSelectedForecastForReceiptFormState(forecastForReceiptFormState)
        "
      />
      <!-- 詳細フィルターモーダル -->
      <FilterModalButton
        :filter-modal-type="'customerForecast'"
        :modal-state="state.detailFilterModalState"
        :select-input-index="1"
        :filter-items="getFilterItemsValue().value"
        @apply="(filteredContents) => store.onAppliedDetailFilter(filteredContents)"
      />
    </div>
    <div class="receive-forecast-business__table-area table-area">
      <div class="table-area__top-accessory">
        <div class="table-area__select-month-area select-month-area">
          <div class="select-month-area__title">
            {{ t('forecastForReceipt_searchReceiveDate') }}
          </div>
          <MonthPicker
            class="select-month-area__calendar"
            :model-value="state.receivedForecastYearMonthInfo"
            :locale="locale"
            :prop-format="t('MonthPickerFormat')"
            @update:model-value="store.onSelectedReceiveMonth($event)"
          />
        </div>
        <Pagination
          v-if="state.tableContents.pagingOptions"
          :paging-options="state.tableContents.pagingOptions"
          :is-loading="state.tableContents.loadingState.status.type === 'loading'"
          class="table-area__pagination"
          @on-change-page="store.onChangePaging($event)"
        />
        <SelectInput
          class="table-area__forecast-length-select"
          :index="22"
          :model-value="t('displayMonthsOfData', { month: state.displayForecastsMonth })"
          :placeholder="''"
          :label="''"
          :filter-type="'DISPLAY_MONTH'"
          :prop-options="monthsOptionsDisplayForecast"
          :is-open-prop="state.isOpenSelectInputType === 'DISPLAY_MONTH'"
          use-teleport
          @click:select-input="() => (state.isOpenSelectInputType = 'DISPLAY_MONTH')"
          @update:value-with-index="(_, index) => store.onSelectedDisplayForecastsMonth(index)"
        />
      </div>
      <div
        v-if="state.tableContents.loadingState.status.type === 'zeroMatch'"
        class="table-area__business-table table-area__business-table--zero-match"
      >
        {{ t('forecastForReceipt_placeholderForZeroMatch') }}
      </div>
      <NewBaseTableContent
        v-else
        :key="state.tableContents.renderingKey"
        class="table-area__business-table table-area__business-table--success"
        :prop-table-content-properties="state.tableContents.tableHeaders"
        :prop-display-objects="store.tableContentItems"
        :prop-loading-status="state.tableContents.loadingState"
        :prop-table-type="'forecastForReceiptAllBusinessTable'"
        @checked="(checkedDisplayIds) => store.onCheckedDisplayIds(checkedDisplayIds)"
        @on-click-sort="(sortKey) => store.sortTableContentItems(sortKey)"
        @on-click-remove-from-forecast="(displayId) => onClickRemoveFromForecastInRow(displayId)"
      />
    </div>
  </div>
  <!-- 受領用内示編集モーダル -->
  <EditForecastModal
    :modal-state="state.editForecastModalState"
    :select-input-index="23"
    @close-modal="() => (state.editForecastModalState = false)"
    @apply="(selectedStaffIdList, _) => store.onApplyEditForecast(selectedStaffIdList)"
  />
  <!-- 仕入先内示作成の確認モーダル -->
  <ConfirmModal
    :modal-state="state.createForecastModalState"
    :title-text="t('forecastForReceipt_createSupplierForecast')"
    :content-text="`${t('forecastForReceipt_descriptionOfCreateSupplierForecast1')}\n${t(
      'forecastForReceipt_descriptionOfCreateSupplierForecast2'
    )}`"
    :confirm-button-text="t('作成')"
    :deny-button-text="t('キャンセル')"
    @close-modal="state.createForecastModalState = false"
    @confirm="store.onConfirmedCreateForecast(supplierForecastRepository)"
  />
  <!-- 受領用内示ダウンロードモーダル -->
  <DownloadForecastForReceiptModal
    v-if="state.downloadForecastForReceiptModalState.modalState"
    :modal-state="state.downloadForecastForReceiptModalState.modalState"
    class="button-area__download-button"
    @close="state.downloadForecastForReceiptModalState.modalState = false"
    @apply="getTemplateCustomerForecastExcelDownload"
  />
  <!-- 客先内示ダウンロードモーダル -->
  <DownloadCustomerForecastModal
    v-if="state.downloadCustomerForecastModalState.modalState"
    :modal-state="state.downloadCustomerForecastModalState.modalState"
    :initial-start-year-month="state.downloadCustomerForecastModalState.startYearMonth"
    :initial-end-year-month="state.downloadCustomerForecastModalState.endYearMonth"
    @close="closeDownloadCustomerForecastModal"
    @apply="onApplyDownloadCustomerForecast"
  />
  <!-- 仕入先内示作成時のローディングモーダル -->
  <InfoModal :modal-state="isLoading(state.createSupplierForecastLoadingState.status)" icon="loading" :enable-outside-click="false">
    <template #title>{{ t('仕入先内示を登録中です。') }}</template>
  </InfoModal>

  <InfoModal
    :modal-state="state.confirmApplyEditModalState"
    icon="info"
    :enable-outside-click="false"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存')"
    @on-next="store.onAppliedConfirmApplyEdit"
    @on-back="store.onCanceledConfirmApplyEdit"
  >
    <template #title>{{ t('編集を完了') }}</template>
    <template #description>{{ t('受領用内示の編集を確定し、変更を保存します。') }}</template>
    <template #footer>
      <div class="confirm-edit-modal__footer" @click="store.onRevokedConfirmApplyEdit">{{ t('保存せずに終了') }}</div>
    </template>
  </InfoModal>
  <!-- 受領用内示作成/削除等用のローディング -->
  <InfoModal :modal-state="isLoading(state.forecastForReceiptLoadingState.state.status)" icon="loading" :enable-outside-click="false">
    <template #title>{{ ForecastForReceiptMenuService.getLoadingText(state.forecastForReceiptLoadingState.type) }}</template>
  </InfoModal>
  <!-- 受領用内示新規作成モーダル -->
  <CreateForecastForReceiptModal
    :modal-state="state.selectedForecastForReceiptMenu === 'CreateForecastForReceipt'"
    :select-input-index="25"
    @close="onClosedForecastForReceiptActionModal"
    @apply="
      (newForecastForReceiptInfo) => {
        onAppliedCreateBaseBusinessForecastForReceipt(newForecastForReceiptInfo);
      }
    "
  />
  <!-- 受領用内示へ関連取引追加モーダル -->
  <AddBaseBusinessForecastForReceiptModal
    :modal-state="state.selectedForecastForReceiptMenu === 'AddBaseBusinessToForecastForReceipt'"
    :select-input-index="26"
    @close="onClosedForecastForReceiptActionModal"
    @apply="
      (selectedForecastForReceiptId) => {
        onAppliedAddBaseBusinessForecastForReceipt(selectedForecastForReceiptId);
      }
    "
  />
  <!-- 受領用内示編集へ入るモーダル -->
  <EditForecastForReceiptModal
    :modal-state="state.selectedForecastForReceiptMenu === 'EditForecastForReceipt'"
    :select-input-index="27"
    @close="onClosedForecastForReceiptActionModal"
    @apply="
      (selectedForecastForReceiptId, selectedForecastForReceiptFormState) => {
        onAppliedEditBaseBusinessForecastForReceipt(selectedForecastForReceiptId, selectedForecastForReceiptFormState);
      }
    "
  />
  <!-- 受領用内示削除確認モーダル（編集モード用） -->
  <ConfirmModal
    :modal-state="state.deleteForecastForReceiptConfirmModalState"
    :title-text="t('受領用内示の削除')"
    :content-text="t('選択した受領用内示を削除します。')"
    :confirm-button-text="t('削除')"
    :deny-button-text="t('キャンセル')"
    @close-modal="onCanceledDeleteForecastForReceiptInEditMode()"
    @confirm="onConfirmedDeleteForecastForReceiptInEditMode()"
  />
  <!-- 受領用内示の情報変更モーダル -->
  <UpdateForecastForReceiptModal
    :modal-state="state.editForecastForReceiptModeState.updateForecastForReceiptModalState"
    :select-input-index="29"
    :target-forecast-for-receipt-id="state.editForecastForReceiptModeState.selectedEditForecastForReceiptId"
    :current-forecast-for-receipt-form-state="state.editForecastForReceiptModeState.selectedForecastForReceiptFormState"
    @close="() => (state.editForecastForReceiptModeState.updateForecastForReceiptModalState = false)"
    @apply="
      (updateForecastForReceiptInfo) => {
        onAppliedUpdateBaseBusinessForecastForReceipt(updateForecastForReceiptInfo);
      }
    "
  />
</template>

<style lang="scss" scoped>
.receive-forecast-business {
  width: 100%;
  min-height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto) minmax(0, 1fr);
  gap: 8px;
  font: Noto Sans JP;
  letter-spacing: 0;
  white-space: nowrap;

  .top-area {
    display: grid;
    grid-auto-flow: column;
    align-items: start;
    justify-content: start;
    gap: 16px;

    .button-area {
      display: grid;
      grid-template-columns: repeat(5, auto);
      gap: 16px;

      &__button {
        border-radius: 4px;
        &--fill {
          background-color: $semanticPrimary;
          font-weight: 700;
          font-size: 14px;
          color: $semanticWhite;
        }
      }

      .edit-forecast-for-receipt-button {
        height: 34px;
        display: grid;
        place-items: center;
        grid-template-columns: repeat(4, auto);
        gap: 8px;

        &--update-name-button {
          height: 34px;
          background-color: $semanticPrimary;
          color: $semanticWhite;
          padding: 0 17px;
          place-items: center;
          line-height: 22px;
          border-radius: 4px;
          border: 1px solid $semanticPrimary;
        }

        &--delete-from-group-button {
          height: 34px;
          background-color: $semanticPrimary;
          color: $semanticWhite;
          padding: 0 17px;
          place-items: center;
          line-height: 22px;
          border-radius: 4px;
          border: 1px solid $semanticPrimary;
        }

        &--delete-forecast-button {
          height: 34px;
          background-color: $semanticError;
          color: $semanticWhite;
          padding: 0 17px;
          place-items: center;
          line-height: 22px;
          border-radius: 4px;
          border: 1px solid $semanticError;
        }

        &--cancel-button {
          height: 34px;
          background-color: $semanticWhite;
          color: $semanticPrimary;
          padding: 0 17px;
          place-items: center;
          line-height: 22px;
          border-radius: 4px;
          border: 1px solid $semanticPrimary;
        }
      }

      .edit-base-business-responsible-user__button {
        width: fit-content;
        height: 34px;
        padding: 0 17px;
        display: grid;
        place-items: center;
        line-height: 22px;

        &--container {
          display: grid;
          grid-template-columns: auto 22px;
          place-items: center;
          gap: 4px;
        }

        &--icon {
          color: white;
        }
      }

      .create-receive-forecast-button {
        width: fit-content;
        height: 34px;
        padding: 0 17px;
        display: grid;
        place-items: center;
        line-height: 22px;

        &__container {
          display: grid;
          grid-template-columns: auto 22px;
          place-items: center;
          gap: 4px;
        }
      }

      &__delete-receive-forecast-button {
        width: 92px;
        height: 34px;
        background-color: $semanticWhite;
        border-radius: 4px;
        border: 1px solid $semanticPrimary;
      }

      &__download-menu-dropdown {
        height: 34px;
        display: grid;
        align-items: center;
      }
    }
  }

  .filter-content {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: start;
    align-items: center;

    &__select-forecast {
      width: 200px;
      height: 32px;
    }
  }

  .select-month-area {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: start;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;

    &__title {
      font-size: 14px;
      line-height: 20.27px;
      font-weight: 400;
      color: $textDefault;
    }

    &__calendar {
      width: 141px;
      height: 36px;
      background-color: $semanticWhite;
      border-radius: 4px;
      border: 1px solid $borderDefault;
      display: grid;
      place-items: center;
    }
  }

  .table-area {
    height: 100%;
    overflow-x: auto;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);

    &__top-accessory {
      display: grid;
      grid-template-columns: repeat(3, auto);
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    &__business-table {
      &--zero-match {
        background-color: $surfaceDefault;
        display: grid;
        place-items: center;
        font-weight: 400;
        font-size: 14px;
        line-height: 18.2px;
        color: $textSecondary;
      }
      &--success {
        overflow-y: scroll !important;
        background-color: $backgroundDefault;
      }
      border: 1px solid $borderDefault;
      min-height: 0 !important;
      max-height: 100% !important;
    }

    &__forecast-length-select {
      width: 162px;
      height: 32px;
    }
  }
}

.detail-filter-modal {
  position: absolute;
  width: 73%;
  z-index: 10;
}

.info-modal {
  &__title {
    color: $textDefault !important;
    font-weight: 700 !important;
    font-size: 22px !important;
    line-height: 28.6px !important;
  }

  &__text {
    color: $textDefault !important;
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 18.2px !important;
  }

  &__confirm-button {
    width: 144px !important;
    height: 40px !important;
    background-color: $semanticPrimary !important;
    color: $semanticWhite !important;
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 16px !important;
    border-radius: 4px !important;
    padding: 10px 24px !important;
  }

  &__cancel-button {
    width: 144px !important;
    height: 40px !important;
    background-color: $semanticWhite !important;
    color: $semanticPrimary !important;
    border: 1px solid $semanticPrimary !important;
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 16px !important;
    border-radius: 4px !important;
    padding: 10px 24px !important;
  }
}

.confirm-edit-modal {
  &__footer {
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    color: $semanticPrimary;
    border-bottom: 1px solid $semanticPrimary;
    width: fit-content;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>

<style lang="scss">
.select-month-area__calendar input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
}
</style>
